/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Localizer from '../../helpers/localization';
import './Slider.css';
import { Setting } from '../../helpers/settings/types';

type DefaultProps = {
  +ariaLabel?: string,
  +ariaLabelledby?: string,
  +labelRenderer?: (value: number) => string,
  +labelStep?: number,
  +setting?: Setting,
  +showValue?: boolean,
  +tickStep?: number,
  +validate?: (value: number) => boolean,
  +valueConverter?: (value: number) => number,
  +valueConverterBack?: (value: number) => number,
  +valueRenderer?: (value: number) => string,
};

type SliderPropType = {
  ...DefaultProps,
  +id: string,
  +max: number,
  +min: number,
  +onChange: (value: number, setting?: Setting) => void,
  +step: number,
  +value: number,
};

const ONE_HUNDRED = 100;

const Slider = ({
  ariaLabel,
  ariaLabelledby,
  id,
  labelRenderer,
  labelStep,
  max,
  min,
  onChange,
  setting,
  showValue,
  step,
  tickStep,
  validate,
  value,
  valueConverter,
  valueConverterBack,
  valueRenderer,
}: SliderPropType): React.Node => {
  const [displayValue, setDisplayValue] = useState<string>('');

  const ticksElement = useMemo<React.Node>(() => {
    if (tickStep === undefined) {
      return null;
    }

    const renderLabel = labelStep !== undefined && labelRenderer !== undefined ? (v: number) => (v % labelStep === 0 ? labelRenderer(v) : null) : (_) => null; // eslint-disable-line no-unused-vars

    const ticks: Array<number> = [];
    for (let i = min; i <= max; i += tickStep) {
      ticks.push(i);
    }
    return (
      <div className='ticks'>
        {ticks.map((tick) => (
          <span className='tick' key={tick}>
            {renderLabel(tick)}
          </span>
        ))}
      </div>
    );
  }, [labelRenderer, labelStep, max, min, tickStep]);

  const update = useCallback(() => {
    setDisplayValue(valueRenderer ? valueRenderer(value) : value.toString());
  }, [value, valueRenderer]);

  useEffect(() => {
    update();
  }, [update, value]);

  const handleOnChange = useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      const {
        currentTarget: { value: newValue },
      } = event;

      const n = parseFloat(newValue);

      if (!isNaN(n) && (!validate || validate(n))) {
        onChange(valueConverterBack ? valueConverterBack(n) : n, setting);
      }
    },
    [onChange, setting, validate, valueConverterBack],
  );

  const currentValue = valueConverter ? valueConverter(value) : value;
  const progressPercent = ((currentValue - min) / (max - min)) * ONE_HUNDRED;

  return (
    <div className='slider'>
      {ticksElement}
      <input
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-valuenow={currentValue}
        aria-valuetext={displayValue}
        id={id}
        max={max}
        min={min}
        onChange={handleOnChange}
        step={step}
        style={{ '--progress': `${progressPercent}%` }}
        type='range'
        value={currentValue}
      />
      <span className='screenReaderOnly'>{Localizer.localize('accessibility.navigation.slider_hint')}</span>
      {showValue ? (
        <div aria-hidden className='currentValue'>
          {displayValue}
        </div>
      ) : null}
    </div>
  );
};

export default Slider;
